import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { cloneData, cloneDataOrNull, isSameData } from '../../dto/data-model-utils.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { Price } from '../price.js';
import { isNotNullOrUndefined } from '../../../utility/null-safety.js';
import { VersionRange } from '../../core/version-range.js';
import { PromoCodeProtocol } from './promo-code-protocol.js';
import { PercentagePromoCodeProtocol } from './percentage-promo-code-protocol.js';
import { FixedPromoCodeProtocol } from './fixed-promo-code-protocol.js';
import { DownToPricePromoCodeProtocol } from './down-to-price-promo-code-protocol.js';
import { SerializationException } from '../../../exceptions/serialization-exception.js';

export enum DiscountType {
  Percentage = 'Percentage',
  Fixed = 'Fixed',
  DownToPrice = 'DownToPrice',
  Legacy = 'Legacy',
  Tracking = 'Tracking'
}

export enum DiscountSource {
  AdminCheckout = 'AdminCheckout',
  OpenTicketPass = 'OpenTicketPass',
  TicketGiftCard = 'TicketGiftCard',
  PromoCode = 'PromoCode',
  OpenAccessPass = 'OpenAccessPass',
  GiftCard = 'GiftCard',
  GlobalGiftCard = 'GlobalGiftCard',
}

export class Discount extends DataModel {
  public static readonly TYPE_ID: string = '8980aeabc4f34c83bcd56b5803c554ac';
  public static readonly SCHEMA_VERSION: number = 6;

  private constructor(
    public readonly businessRef: BusinessModuleRef,
    public readonly voucherId: string | null,
    public readonly voucherValue: Price | null,
    public readonly code: string | null,
    public readonly type: DiscountType,
    public readonly fixedPrice: Price | null,
    public readonly percentage: number | null,
    public readonly source: DiscountSource,
  ) {
    super();

    if(isNotNullOrUndefined(percentage) && (percentage < 0 || percentage > 100)) {
      throw new Error('Invalid discount value');
    }
  }

  get $typeId(): string {
    return Discount.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(Discount.SCHEMA_VERSION);
  }

  static create(opt: {
    businessRef: BusinessModuleRef,
    voucherId: string | null,
    voucherValue: Price | null,
    code: string | null,
    type: DiscountType,
    fixedPrice: Price | null,
    percentage: number | null,
    source: DiscountSource,
  }): Discount {
    return new Discount(
        opt.businessRef,
        opt.voucherId,
        opt.voucherValue,
        opt.code,
        opt.type,
        opt.fixedPrice,
        opt.percentage,
        opt.source,
    );
  }

  static legacy(opt: { businessRef: BusinessModuleRef, voucherId: string, code: string, percentage: number }): Discount {
    return this.create({
      businessRef: opt.businessRef,
      voucherId: opt.voucherId,
      voucherValue: null,
      code: opt.code,
      type: DiscountType.Legacy,
      fixedPrice: null,
      percentage: opt.percentage,
      source: DiscountSource.PromoCode,
    });
  }

  static percentage(opt: {
    businessRef: BusinessModuleRef,
    voucherId: string | null,
    voucherValue: Price | null,
    code: string | null,
    percentage: number,
    source: DiscountSource,
  }): Discount {
    return this.create({
      businessRef: opt.businessRef,
      voucherId: opt.voucherId,
      voucherValue: opt.voucherValue,
      code: opt.code,
      type: DiscountType.Percentage,
      fixedPrice: null,
      percentage: opt.percentage,
      source: opt.source,
    });
  }

  static fixed(opt: {
    businessRef: BusinessModuleRef,
    voucherId: string | null,
    code: string | null,
    value: Price,
    source: DiscountSource,
  }): Discount {
    return this.create({
      businessRef: opt.businessRef,
      voucherId: opt.voucherId,
      voucherValue: opt.value.clone(),
      code: opt.code,
      type: DiscountType.Fixed,
      fixedPrice: opt.value.clone(),
      percentage: null,
      source: opt.source,
    });
  }

  static downToPrice(opt: {
    businessRef: BusinessModuleRef,
    voucherId: string | null,
    code: string | null,
    value: Price,
    source: DiscountSource,
  }): Discount {
    return this.create({
      businessRef: opt.businessRef,
      voucherId: opt.voucherId,
      voucherValue: null,
      code: opt.code,
      type: DiscountType.DownToPrice,
      fixedPrice: opt.value.clone(),
      percentage: null,
      source: opt.source,
    });
  }

  static tracking(opt: {
    businessRef: BusinessModuleRef,
    voucherId: string,
    code: string,
    source: DiscountSource,
  }): Discount {
    return this.create({
      businessRef: opt.businessRef,
      voucherId: opt.voucherId,
      voucherValue: null,
      code: opt.code,
      type: DiscountType.Tracking,
      fixedPrice: null,
      percentage: null,
      source: opt.source,
    });
  }

  static promoCode(opt: { businessRef: BusinessModuleRef, voucherId: string, code: string, protocol: PromoCodeProtocol }): Discount {
    const createDiscount = (type: DiscountType, percentage: number | null, fixedPrice: Price | null): Discount => {
      return this.create({
        businessRef: opt.businessRef,
        voucherId: opt.voucherId,
        voucherValue: null,
        code: opt.code,
        type: type,
        fixedPrice: fixedPrice,
        percentage: percentage,
        source: DiscountSource.PromoCode,
      });
    };

    if(PercentagePromoCodeProtocol.assertType(opt.protocol)) {
      return createDiscount(DiscountType.Percentage, opt.protocol.discount, null);
    } if(FixedPromoCodeProtocol.assertType(opt.protocol)) {
      return createDiscount(DiscountType.Fixed, null, opt.protocol.price);
    } if(DownToPricePromoCodeProtocol.assertType(opt.protocol)) {
      return createDiscount(DiscountType.DownToPrice, null, opt.protocol.price);
    }
    else {
      throw new Error(`Unknown promo code protocol: ${opt.protocol.$typeId}`);
    }
  }

  static fromMap(context: SerializationContext, map: SerializedData): Discount {
    return this.create({
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      voucherId: MapUtils.getStringOrNull(map, 'voucherId'),
      voucherValue: Price.decodeOrNull(MapUtils.getStringOrNull(map, 'voucherValue')),
      code: MapUtils.getStringOrNull(map, 'code'),
      type: toEnumFromString(MapUtils.getString(map, 'type'), DiscountType),
      fixedPrice: Price.decodeOrNull(MapUtils.getStringOrNull(map, 'fixedPrice')),
      percentage: MapUtils.getNumberOrNull(map, 'percentage'),
      source: toEnumFromString(MapUtils.getString(map, 'source'), DiscountSource),
    });
  }

  toMap(_context: SerializationContext): SerializedData {
    return {
      'businessRef': this.businessRef.encode(),
      'voucherId': this.voucherId,
      'voucherValue': this.voucherValue?.encode() ?? null,
      'code': this.code,
      'type': fromEnumAsString(this.type),
      'fixedPrice': this.fixedPrice?.encode() ?? null,
      'percentage': this.percentage,
      'source': fromEnumAsString(this.source),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.voucherId === this.voucherId &&
        isSameData(other.businessRef, this.businessRef) &&
        other.code === this.code &&
        other.type === this.type &&
        other.source === this.source &&
        other.percentage === this.percentage &&
        isSameData(other.voucherValue, this.voucherValue) &&
        isSameData(other.fixedPrice, this.fixedPrice);
  }

  copyWith({
    businessRef = cloneData(this.businessRef),
    voucherId = this.voucherId,
    voucherValue = cloneDataOrNull(this.voucherValue),
    code = this.code,
    type = this.type,
    fixedPrice = cloneDataOrNull(this.fixedPrice),
    percentage = this.percentage,
    source = this.source,
  }: {
    businessRef?: BusinessModuleRef,
    voucherId?: string | null,
    voucherValue?: Price | null,
    code?: string | null,
    type?: DiscountType,
    fixedPrice?: Price | null,
    percentage?: number | null,
    source?: DiscountSource,
  } = {}): this {
    return Discount.create({
      businessRef,
      voucherId,
      voucherValue,
      code,
      type,
      fixedPrice,
      percentage,
      source,
    }) as this;
  }
}

export class DiscountBuilder extends DataModelBuilder<Discount> {
  build(context: SerializationContext, map: SerializedData): Discount {
    return Discount.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(Discount.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(Discount.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version we added `code` of type `string`.
    if(version < 2) {
      map['code'] = map['voucherId'];
      version = 2;
    }

    // In this version we added `voucherValue` of type `Price | null`.
    if(version < 3) {
      version = 3;
    }

    // Version 4:
    // - make `voucherId` be of type `string | null`,
    // - make `code` be of type `string | null`.
    if(version < 4) {
      version = 4;
    }

    // Version 5:
    // - added `isPass` of type `boolean`
    if(version < 5) {
      const code = MapUtils.getStringOrNull(map, 'code');
      const isPass = code?.startsWith('OFP-') ?? false;
      map['isPass'] = isPass;
      version = 5;
    }

    // Version 6:
    // - replaces `isPromoCode` and `isPass` with `source` of type `DiscountSource`
    if(version < 6) {
      const source = this.extractDiscountSourceFromLegacyData(version, map);
      map['source'] = fromEnumAsString(source);
      version = 6;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }

  migrateFromCurrentSchemaVersion(
      context: SerializationContext,
      model: Discount,
      map: SerializedData
  ): SerializedData {
    let version = model.$schemaVersion.to;

    if(version >= 6) {
      version = 1;

      map['isPromoCode'] = model.source === DiscountSource.PromoCode;
      map['isPass'] = model.source === DiscountSource.OpenAccessPass;
      map['$schema_version'] = VersionRange.encodedBetween(1, 6);
    }

    return map;
  }

  private extractDiscountSourceFromLegacyData(schemaVersion: number, map: SerializedData): DiscountSource {
    if(schemaVersion >= 6) {
      throw new SerializationException(
          'Failed to extract discount source. Current schema version no longer stores relevant data.',
          { schemaVersion }
      );
    }

    const isPromoCode = MapUtils.getBoolean(map, 'isPromoCode');
    const isPass = MapUtils.getBoolean(map, 'isPass');
    const code = MapUtils.getStringOrNull(map, 'code');
    const type = toEnumFromString(MapUtils.getString(map, 'type'), DiscountType);

    if(code == null) {
      return DiscountSource.AdminCheckout;
    }
    if(isPromoCode) {
      return DiscountSource.PromoCode;
    }
    if(isPass) {
      return DiscountSource.OpenAccessPass;
    }

    switch(type) {
      case DiscountType.Fixed:
        return DiscountSource.TicketGiftCard;
      case DiscountType.Percentage:
        return DiscountSource.OpenTicketPass;
      default:
        return DiscountSource.PromoCode;
    }
  }
}
